
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import AlreadyHaveTrailerAttached from '@/components/AlreadyHaveTrailerAttached.vue';
import LoadingModal from '@/components/LoadingModal.vue';
import SomethingWrongOverlay from '@/components/SomethingWrongOverlay.vue';
import ReturnOptions from '@/components/ReturnOptions.vue';
import getTrailersAlreadyAttachedToTruck from '@/shared/mixins/getTrailersAlreadyAttachedToTruck';
import { isNullOrUndefined, getReturnAppName } from '@/shared/helpers';
import Driver from '@/types/Driver';
import Truck from '@/types/Truck';
import FbaApiError from '@/types/FbaApiError';
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import Trailer from '@/types/Trailer';
import DriverLoaded from '@/components/DriverLoaded.vue';
import { ActionTypes } from '@/store/actionTypes';

export default defineComponent({
    name: 'EnterTrailerRedirect',
    components: {
        LoadingModal,
        AlreadyHaveTrailerAttached,
        SomethingWrongOverlay,
        ReturnOptions,
        DriverLoaded,
    },
    computed: {
        ...mapGetters({
            truck: 'getTruck',
            driver: 'getDriver',
            error: 'getError',
            trailerAssociationsEnabled: 'trailerAssociationsEnabled',
        }),
        alreadyAttached(): boolean {
            return (
                !isNullOrUndefined(this.currentAttachedTrailers) &&
                this.currentAttachedTrailers.length > 0
            );
        },
        hasError(): boolean {
            return this.error instanceof FbaApiError;
        },
        alertTitle() {
            return this.trailerAssociationsEnabled === false
                ? 'Dry Drivers Only'
                : 'Unable to Identify Truck Number';
        },
        alertMessage() {
            const appName = getReturnAppName(
                this.$route?.params?.company as string
            );
            const nonDryMessage = `We show you are a non-dry driver. Please return to your ${appName} dashboard and utilize the Non-Dry Attach/Detach empty trailer button.`;
            const noTruckMessage =
                'Please contact your DM/DL to resolve this issue.';

            return this.trailerAssociationsEnabled === false
                ? nonDryMessage
                : noTruckMessage;
        },
        shouldAlert() {
            return (
                this.trailerAssociationsEnabled === false ||
                this.truckNotFound === true
            );
        },
    },
    mixins: [getTrailersAlreadyAttachedToTruck],
    data: (): {
        currentAttachedTrailers: Array<Trailer>;
        showLoading: boolean;
        truckNotFound: boolean;
        showLoadedTrailerModal: boolean;
    } => {
        return {
            currentAttachedTrailers: [],
            showLoading: false,
            truckNotFound: false,
            showLoadedTrailerModal: false,
        };
    },
    beforeRouteEnter(
        to: RouteLocationNormalized,
        from: RouteLocationNormalized,
        next: NavigationGuardNext
    ) {
        if (from.name === 'EnterTrailer') next({ name: 'DriverLanding' });

        next();
    },
    methods: {
        async tryRouteToManualAttach(): Promise<void> {
            this.currentAttachedTrailers = [];
            await this.setupAttachedTrailerOrRoute();
        },
        async fetchDriver() {
            await this.$store.dispatch(ActionTypes.FETCH_DRIVER, {
                company: this.driver.company,
                driverCode: this.driver.driverCode,
            });
        },
        async setupAttachedTrailerOrRoute(): Promise<void> {
            this.showLoading = true;

            const attachedTrailers =
                await this.getTrailersAlreadyAttachedToTruck();
            if (
                !isNullOrUndefined(attachedTrailers) &&
                Array.isArray(attachedTrailers)
            ) {
                this.currentAttachedTrailers = attachedTrailers;

                await this.fetchDriver();

                if (this.driver.isLoaded === true) {
                    this.showLoadedTrailerModal = true;
                }
            } else {
                this.$router.push({ name: 'EnterTrailer' });
            }

            this.showLoading = false;
        },
        routeToLanding(): void {
            this.$router.push({ name: 'DriverLanding' });
        },
    },
    watch: {
        driver: {
            handler() {
                if (
                    this.driver instanceof Driver &&
                    this.driver?.truckCode?.trim() === ''
                ) {
                    this.truckNotFound = true;
                }
            },
        },
        truck: {
            async handler() {
                if (
                    this.trailerAssociationsEnabled &&
                    this.truck instanceof Truck
                ) {
                    await this.setupAttachedTrailerOrRoute();
                }
            },
        },
    },
});
